import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_ENDPOINTS, API_SERVER_URL } from "./endpoints";
import { API_METHODS } from "./constants";
import { handleAuthErrorCode } from "./errorCodes";
import {
  BlankTemplatePayload,
  BulkUploadDataPayload,
  CreateBulkUploadRequestPayload,
  CreateBulkUploadRequestResponse,
  GetBulkProcessStatusResponse,
  RunBulkProcessResponse,
  RunBulkUploadProcessPayload,
} from "../types/BulkUpload";
import {
  appBaseQuery,
  baseQueryWithAuthHandling,
  preparedAuthHeader,
} from "../utils/utils";
import { localStorageKeys } from "../../utils/localStorageItems";

// Fetch request to download report
export const downloadTemplate = (
  params: BlankTemplatePayload,
  type: string,
  onSuccess?: () => void,
  onError?: () => void
) => {
  const additionalHeader: HeadersInit = {};
  additionalHeader["Content-Type"] = "text/csv";

  fetch(
    `${API_SERVER_URL}${API_ENDPOINTS.BULK_UPLOAD.BLANK_TEMPLATE}?${new URLSearchParams(params as any).toString()}`,
    {
      method: API_METHODS.GET,
      headers: {
        Authorization: preparedAuthHeader(
          localStorage.getItem(localStorageKeys.accessToken)
        ),
        ...additionalHeader,
      },
    }
  )
    .then((resp) => {
      if (!resp.ok) {
        onError && onError();
        return;
      }
      return resp.blob();
    })
    .then((blob) => {
      if (!blob) {
        onError && onError();
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // the filename you want
      a.download = params?.with_data
        ? `${type}_${new Date().getTime().toString()}.csv`
        : `${type}_Blank_Template_${new Date().getTime().toString()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess && onSuccess();
    })
    .catch(() => {
      onError && onError();
    });
};

// Fetch request to bulk upload data
export const bulkUploadData = (
  params: BulkUploadDataPayload,
  signal: AbortSignal,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
) => {
  fetch(params.s3Url, {
    method: API_METHODS.PUT,
    body: params.data,
    signal: signal,
  })
    .then((response) => {
      if (response.ok) {
        onSuccess && onSuccess(response);
      } else {
        onError && onError("File upload failed");
      }
    })
    .catch((error) => {
      onError && onError(error);
    });
};

export enum BulkApiTags {
  RESET_STATUS = "ResetStatus",
}

export const BulkAPIs = createApi({
  reducerPath: "bulkApi",
  tagTypes: [BulkApiTags.RESET_STATUS],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(appBaseQuery.withAuth, args, api, extraOptions),
  endpoints: (builder) => ({
    // Create bulk upload request
    createBulkUploadRequest: builder.mutation<
      CreateBulkUploadRequestResponse,
      CreateBulkUploadRequestPayload
    >({
      query: (params: CreateBulkUploadRequestPayload) => {
        return {
          url: API_ENDPOINTS.BULK_UPLOAD.CREATE_BULK_UPLOAD_REQUEST,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CreateBulkUploadRequestResponse) => {
        return response;
      },
    }),
    // Run bulk upload process
    runBulkUploadProcess: builder.mutation<
      RunBulkProcessResponse,
      RunBulkUploadProcessPayload
    >({
      query: (params: RunBulkUploadProcessPayload) => {
        return {
          url: API_ENDPOINTS.BULK_UPLOAD.RUN_PROCESS,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: RunBulkProcessResponse) => {
        return response;
      },
    }),
    // Get process status
    getBulkProcessStatus: builder.query<GetBulkProcessStatusResponse, number>({
      query: (id: number) => {
        return {
          url: API_ENDPOINTS.BULK_UPLOAD.PROCESS_STATUS(id),
          method: API_METHODS.GET,
          params: { timestamp: Math.random() },
        };
      },
      providesTags: [BulkApiTags.RESET_STATUS],
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: GetBulkProcessStatusResponse) => {
        return response;
      },
    }),
  }),
});

export const {
  useCreateBulkUploadRequestMutation,
  useRunBulkUploadProcessMutation,
  useGetBulkProcessStatusQuery,
} = BulkAPIs;
